/**
 * Loads Spotlight patient summary when a patient hit is active.
 */

import { useEffect, useRef, useState } from 'react';
import {
  fetchPatientSpotlightSummary,
  type PatientSpotlightSummary,
} from './fetchPatientSpotlightSummary';

export type UsePatientSpotlightSummaryArgs = {
  patientId: string | null;
  includeNextAppointment: boolean;
  includeOutstandingBalance: boolean;
};

export type UsePatientSpotlightSummaryResult = {
  loading: boolean;
  error: boolean;
  summary: PatientSpotlightSummary | null;
};

export const usePatientSpotlightSummary = ({
  patientId,
  includeNextAppointment,
  includeOutstandingBalance,
}: UsePatientSpotlightSummaryArgs): UsePatientSpotlightSummaryResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [summary, setSummary] = useState<PatientSpotlightSummary | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!patientId || (!includeNextAppointment && !includeOutstandingBalance)) {
      setLoading(false);
      setError(false);
      setSummary(null);
      return undefined;
    }

    const seq = ++seqRef.current;
    const controller = new AbortController();
    setLoading(true);
    setError(false);

    void fetchPatientSpotlightSummary(patientId, {
      includeNextAppointment,
      includeOutstandingBalance,
      signal: controller.signal,
    })
      .then((data) => {
        if (seq !== seqRef.current) return;
        setSummary(data);
        setLoading(false);
      })
      .catch(() => {
        if (seq !== seqRef.current || controller.signal.aborted) return;
        setSummary(null);
        setError(true);
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [patientId, includeNextAppointment, includeOutstandingBalance]);

  return { loading, error, summary };
};
